import { IConstructor } from "../models/IConstructor";
import HttpErrorResponseModel from "../models/HttpErrorResponseModel";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import HttpUtility from "./HttpUtility";

type FlattenIfArray<T> = T extends (infer R)[] ? R : T;
type SingleItemOrArray<T> = T extends [] ? T[] : T;

class FileResponse extends Blob {
    constructor(data: BlobPart) {
        super([data]);
    }
}

export default class EffectUtility {
    public static async getToModel<T>(
        Model: IConstructor<FlattenIfArray<T>>,
        endpoint: string,
        params?: any,
        requestConfig?: AxiosRequestConfig
    ): Promise<SingleItemOrArray<T> | HttpErrorResponseModel> {
        const response: AxiosResponse | HttpErrorResponseModel = await HttpUtility.get(endpoint, params, {
            timeout: 600000,
            ...requestConfig,
        });

        return EffectUtility._restModelCreator<T>(Model, response);
    }

    public static async getFile(
        endpoint: string,
        params?: any,
        progressHandler?: (status: number) => void
    ): Promise<Blob | HttpErrorResponseModel> {
        let responseFormat = (data: ArrayBuffer) => {
            let result: Blob | HttpErrorResponseModel;
            try {
                result = JSON.parse(new TextDecoder().decode(data)) as HttpErrorResponseModel;
            } catch (e) {
                result = new Blob([data]);
            }
            return result;
        };
        let requestConfig = { responseType: "arraybuffer", transformResponse: [responseFormat] } as AxiosRequestConfig;
        if (progressHandler) {
            requestConfig.onDownloadProgress = (progressEvent: any) => {
                const totalLength: number =
                    (progressEvent.lengthComputable
                        ? progressEvent.total
                        : progressEvent.target.getResponseHeader("content-length") ||
                          progressEvent.target.getResponseHeader("x-decompressed-content-length")) ?? 0;
                const loadedLength: number = progressEvent.loaded ?? 0;
                if (totalLength !== null) {
                    const status = Math.round((loadedLength * 100) / totalLength);
                    progressHandler(status);
                }
            };
        }
        return await this.getToModel<FileResponse>(FileResponse, endpoint, params, requestConfig);
    }

    public static async postFormDataToModel<T>(
        Model: IConstructor<FlattenIfArray<T>>,
        endpoint: string,
        data?: any,
        progressHandler?: (status: number) => void
    ): Promise<SingleItemOrArray<T> | HttpErrorResponseModel> {
        let formData = new FormData();
        Object.keys(data).forEach((propKey) => {
            const propValue = data[propKey];
            if (formData.has(propKey)) {
                formData.append(propKey, propValue);
            } else {
                formData.set(propKey, propValue);
            }
        });
        let requestConfig: AxiosRequestConfig = { headers: { "Content-Type": "multipart/form-data" }, data: formData };
        if (progressHandler) {
            requestConfig.onUploadProgress = (progressEvent: any) => {
                const totalLength: number =
                    (progressEvent.lengthComputable
                        ? progressEvent.total
                        : progressEvent.target.getResponseHeader("content-length") ||
                          progressEvent.target.getResponseHeader("x-decompressed-content-length")) ?? 0;
                const loadedLength: number = progressEvent.loaded ?? 0;
                if (totalLength !== null) {
                    const status = Math.round((loadedLength * 100) / totalLength);
                    progressHandler(status);
                }
            };
        }

        return await this.postToModel<T>(Model, endpoint, data, requestConfig);
    }

    public static async postToModel<T>(
        Model: IConstructor<FlattenIfArray<T>>,
        endpoint: string,
        data?: any,
        requestConfig?: AxiosRequestConfig
    ): Promise<SingleItemOrArray<T> | HttpErrorResponseModel> {
        const response: AxiosResponse | HttpErrorResponseModel = await HttpUtility.post(endpoint, data, {
            timeout: 600000,
            ...requestConfig,
        });

        return EffectUtility._restModelCreator<T>(Model, response);
    }

    public static async putToModel<T>(
        Model: IConstructor<FlattenIfArray<T>>,
        endpoint: string,
        data?: any
    ): Promise<SingleItemOrArray<T> | HttpErrorResponseModel> {
        const response: AxiosResponse | HttpErrorResponseModel = await HttpUtility.put(endpoint, data);

        return EffectUtility._restModelCreator<T>(Model, response);
    }

    public static async deleteToModel<T>(
        Model: IConstructor<FlattenIfArray<T>>,
        endpoint: string,
        params?: any
    ): Promise<SingleItemOrArray<T> | HttpErrorResponseModel> {
        const response: AxiosResponse | HttpErrorResponseModel = await HttpUtility.delete(endpoint, params);

        return EffectUtility._restModelCreator<T>(Model, response);
    }

    private static _restModelCreator<T>(
        Model: IConstructor<FlattenIfArray<T>>,
        response: AxiosResponse | HttpErrorResponseModel
    ): SingleItemOrArray<T> | HttpErrorResponseModel {
        if (response instanceof HttpErrorResponseModel) {
            return response;
        }
        return !Array.isArray(response.data)
            ? new Model(response.data)
            : (response.data.map((json) => new Model(json)) as any);
    }
}
